import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface CartItem {
    id: string                    // crypto.randomUUID() — client-side only
    projectId: string
    projectName: string
    frontPageId: number
    frontPageThumbnail?: string   // Base64 or URL for preview
    backPageId: number | null
    backPageThumbnail?: string
    materialId: number
    materialName: string
    sizeId: number
    sizeName: string
    quantityMillares: number
    pricePerMillar: number
    subtotal: number
}

interface CartState {
    items: CartItem[]
    shippingAddressId: number | null
    shippingCost: number
    notes: string
}

interface CartActions {
    addItem: (item: Omit<CartItem, 'id' | 'subtotal'>) => void
    removeItem: (id: string) => void
    updateItemQuantity: (id: string, quantity: number) => void
    setShippingAddress: (addressId: number, cost: number) => void
    setNotes: (notes: string) => void
    clearCart: () => void
}

type Store = CartState & CartActions

const MAX_CART_ITEMS = 20

export const useCartStore = create<Store>()(
    persist(
        (set, get) => ({
            // State
            items: [],
            shippingAddressId: null,
            shippingCost: 0,
            notes: '',

            // Actions
            addItem: (itemData) => {
                if (get().items.length >= MAX_CART_ITEMS) return
                const item: CartItem = {
                    ...itemData,
                    id: crypto.randomUUID(),
                    subtotal: itemData.pricePerMillar * itemData.quantityMillares,
                }
                set((state) => ({ items: [...state.items, item] }))
            },

            removeItem: (id) => {
                set((state) => ({ items: state.items.filter((i) => i.id !== id) }))
            },

            updateItemQuantity: (id, quantity) => {
                set((state) => ({
                    items: state.items.map((item) =>
                        item.id === id
                            ? { ...item, quantityMillares: quantity, subtotal: item.pricePerMillar * quantity }
                            : item
                    ),
                }))
            },

            setShippingAddress: (addressId, cost) => {
                set({ shippingAddressId: addressId, shippingCost: cost })
            },

            setNotes: (notes) => set({ notes }),

            clearCart: () => set({
                items: [],
                shippingAddressId: null,
                shippingCost: 0,
                notes: '',
            }),
        }),
        {
            name: 'impresiones-cart',
        }
    )
)

// Derived selectors (outside store for performance)
export const selectItemsTotal = (state: CartState): number =>
    state.items.reduce((sum, item) => sum + item.subtotal, 0)

export const selectTotal = (state: CartState): number =>
    selectItemsTotal(state) + state.shippingCost

export const selectItemCount = (state: CartState): number =>
    state.items.length
